import { CompressedCheckbox } from "@components/CompressedCheckbox";
import { DropdownWithSearch } from "../../components/DropdownWithSearch";
import { useForm, useFormField } from "@hooks/useForm";
import { Button, Dialog, DropdownTrigger, Error, RadioButtons, Select } from "@narmi/design_system";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { useEffect, useMemo, useState } from "react";
import { $staffApi } from "../../api";
import type { operations } from "../../api_utils/schema-staff";
import styles from "./ReportsPrefilteringModal.module.css";

export type ReportType =
  operations["reports_columns_retrieve"]["parameters"]["path"]["report_type"];

function ReportsPrefilteringModal({
  isOpen,
  onClose,
  reportType,
  reportTitle,
}: {
  isOpen: boolean;
  onClose: () => void;
  reportType: ReportType;
  reportTitle: string;
}) {
  const { sendNotification } = useNotificationContext();
  const [step, setStep] = useState<1 | 2>(1);
  const [dateMode, setDateMode] = useState<"month" | "custom">("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [columnSearchValue, setColumnSearchValue] = useState("");

  // Step 1: Date selection fields
  const selectedMonth = useFormField("", {
    validator: (value) => {
      if (dateMode === "month" && !value) {
        return "Please select a month";
      }
      if (dateMode === "month" && value && selectedYear.value) {
        const selectedDate = `${selectedYear.value}-${value}`;
        if (!availableMonths.includes(selectedDate)) {
          return "A report is not available for this date";
        }
      }
      return null;
    },
  });

  const selectedYear = useFormField("", {
    validator: (value) => {
      if (dateMode === "month" && !value) {
        return "Please select a year";
      }
      if (dateMode === "month" && value && selectedMonth.value) {
        const selectedDate = `${value}-${selectedMonth.value}`;
        if (!availableMonths.includes(selectedDate)) {
          return "A report is not available for this date";
        }
      }
      return null;
    },
  });

  const customStartDate = useFormField("", {
    validator: (value) => {
      if (dateMode === "custom" && !value) {
        return "Please select start date";
      }
      if (dateMode === "custom" && value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayString = today.toISOString().split("T")[0];
        if (value > todayString) {
          return "Start date cannot be in the future";
        }
        if (customEndDate.value && value > customEndDate.value) {
          return "Start date must be before or equal to end date";
        }
        if (customEndDate.value) {
          const startYM = value.substring(0, 7);
          const endYM = customEndDate.value.substring(0, 7);
          const hasAvailableData = availableMonths.some(
            (month) => month >= startYM && month <= endYM
          );
          if (!hasAvailableData) {
            return "A report is not available for this date range";
          }
        }
      }
      return null;
    },
  });

  const customEndDate = useFormField("", {
    validator: (value) => {
      if (dateMode === "custom" && !value) {
        return "Please select end date";
      }
      if (dateMode === "custom" && value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayString = today.toISOString().split("T")[0];
        if (value > todayString) {
          return "End date cannot be in the future";
        }
        if (customStartDate.value && customStartDate.value > value) {
          return "Start date must be before or equal to end date";
        }
      }
      return null;
    },
  });

  // Step 2: Column selection fields
  const selectedColumns = useFormField<string[]>([], {
    validator: (value) => {
      if (value.length === 0) {
        return "Please select at least one column";
      }
      return null;
    },
  });

  // Step 1 form
  const dateSelectionForm = useForm(
    {
      selectedMonth,
      selectedYear,
      customStartDate,
      customEndDate,
    },
    {
      onSubmit: () => {
        let start: string;
        let end: string;

        if (dateMode === "month") {
          start = `${selectedYear.value}-${selectedMonth.value}`;
          end = `${selectedYear.value}-${selectedMonth.value}`;
        } else {
          start = customStartDate.value;
          end = customEndDate.value;
        }

        setStartDate(start);
        setEndDate(end);
        setStep(2);
      },
    }
  );

  // Step 2 form
  const columnSelectionForm = useForm(
    {
      selectedColumns,
    },
    {
      parent: dateSelectionForm,
      onSubmit: () => {
        let startDateValue: string;
        let endDateValue: string;

        if (dateMode === "month") {
          startDateValue = `${selectedYear.value}-${selectedMonth.value}`;
          endDateValue = `${selectedYear.value}-${selectedMonth.value}`;
        } else {
          startDateValue = customStartDate.value;
          endDateValue = customEndDate.value;
        }

        const columns = selectedColumns.value.join(",");
        const downloadUrl = `/api/reports/${reportType}/render?start_date=${encodeURIComponent(
          startDateValue
        )}&end_date=${encodeURIComponent(endDateValue)}&columns=${encodeURIComponent(columns)}`;

        // Trigger download via anchor element
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${reportType}_${startDateValue}_${endDateValue}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        onClose();
      },
    }
  );

  // Fetch available date ranges
  const {
    data: dateRangesData,
    isLoading: loadingDates,
    error: dateRangesError,
  } = $staffApi.useQuery(
    "get",
    "/api/reports/{report_type}/date-ranges/",
    {
      params: { path: { report_type: reportType } },
    },
    {
      enabled: isOpen && !!reportType,
    }
  );

  const availableMonths = useMemo(
    () => dateRangesData?.date_ranges || [],
    [dateRangesData?.date_ranges]
  );
  const noReportsAvailable =
    !loadingDates && isOpen && availableMonths.length === 0 && !!reportType;

  // Fetch columns for selected date range
  const {
    data: columnsData,
    isLoading: loadingColumns,
    error: columnsError,
  } = $staffApi.useQuery(
    "get",
    "/api/reports/{report_type}/columns/",
    {
      params: {
        path: { report_type: reportType },
      },
    },
    {
      enabled: step === 2 && !!startDate && !!endDate,
    }
  );

  const availableColumns = useMemo(() => columnsData?.columns || [], [columnsData?.columns]);

  const filteredColumns =
    columnSearchValue === ""
      ? availableColumns
      : availableColumns.filter((column) =>
          column.toLowerCase().includes(columnSearchValue.trim().toLowerCase())
        );

  // Initialize selected month/year from available dates
  useEffect(() => {
    if (availableMonths.length > 0 && !selectedMonth.value && !selectedYear.value) {
      const mostRecent = availableMonths[0]; // Already sorted descending
      const [year, month] = mostRecent.split("-");
      selectedMonth.setValue(month, false);
      selectedYear.setValue(year, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMonths]);

  // Auto-select all columns when they load
  useEffect(() => {
    if (availableColumns.length > 0 && selectedColumns.value.length === 0) {
      selectedColumns.setValue(availableColumns, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableColumns]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setDateMode("month");
      dateSelectionForm.reset();
      columnSelectionForm.reset();
      setStartDate("");
      setEndDate("");
      setIsColumnDropdownOpen(false);
      setColumnSearchValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isColumnDropdownOpen) {
      setColumnSearchValue("");
    }
  }, [isColumnDropdownOpen]);

  // Handle errors
  useEffect(() => {
    if (dateRangesError) {
      sendNotification({
        text: "Failed to load available date ranges",
        type: "negative",
      });
    }
  }, [dateRangesError, sendNotification]);

  useEffect(() => {
    if (columnsError) {
      sendNotification({
        text: "Failed to load available columns",
        type: "negative",
      });
    }
  }, [columnsError, sendNotification]);

  const handleBack = () => {
    setStep(1);
    selectedColumns.setError(null);
  };

  const handleColumnToggle = (column: string) => {
    selectedColumns.setValue((oldColumns) => {
      const newColumns = new Set(oldColumns);
      if (newColumns.has(column)) {
        newColumns.delete(column);
      } else {
        newColumns.add(column);
      }
      return [...newColumns];
    });
  };

  // Get today's date in YYYY-MM-DD format for max date validation
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayString = today.toISOString().split("T")[0];

  // Generate month options
  const monthOptions = [
    { name: "January", value: "01" },
    { name: "February", value: "02" },
    { name: "March", value: "03" },
    { name: "April", value: "04" },
    { name: "May", value: "05" },
    { name: "June", value: "06" },
    { name: "July", value: "07" },
    { name: "August", value: "08" },
    { name: "September", value: "09" },
    { name: "October", value: "10" },
    { name: "November", value: "11" },
    { name: "December", value: "12" },
  ];

  // Generate year options from available date ranges
  const yearOptions = Array.from(new Set(availableMonths.map((date) => date.split("-")[0])))
    .sort((a, b) => parseInt(b) - parseInt(a))
    .map((year) => ({ name: year, value: year }));

  return (
    <Dialog
      isOpen={isOpen}
      title={`Download ${reportTitle}`}
      onUserDismiss={onClose}
      footer={
        step === 1 ? (
          <div style={{ textAlign: "right" }}>
            <Button label="Cancel" kind="negative" onClick={onClose} />
            <span className="margin--left--s">
              <Button
                label="Next"
                onClick={dateSelectionForm.submit}
                disabled={loadingDates || noReportsAvailable}
              />
            </span>
          </div>
        ) : (
          <div style={{ textAlign: "right" }}>
            <Button label="Back" kind="negative" onClick={handleBack} />
            <span className="margin--left--s">
              <Button
                label="Download"
                onClick={columnSelectionForm.submit}
                disabled={loadingColumns}
              />
            </span>
          </div>
        )
      }
    >
      {step === 1 ? (
        <>
          <div className="padding--top--m">
            <div className={styles.DateRadioButtons}>
              <RadioButtons
                kind="row"
                name="date_mode"
                options={{
                  Month: "month",
                  Custom: "custom",
                }}
                value={dateMode}
                onChange={(e) => setDateMode(e.target.value as "month" | "custom")}
              />
            </div>

            {dateMode === "month" ? (
              noReportsAvailable ? (
                <p style={{ color: "var(--color-errorDark)", margin: "8px 0" }}>
                  No reports available for this report type.
                </p>
              ) : (
                <div style={{ display: "flex", gap: "20px" }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      label="Month"
                      value={monthOptions.find((m) => m.value === selectedMonth.value)?.value}
                      onChange={(value: string) => selectedMonth.setValue(value)}
                      disabled={loadingDates}
                    >
                      {monthOptions.map((month) => (
                        <Select.Item value={month.value} key={month.value}>
                          {month.name}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Select
                      label="Year"
                      value={yearOptions.find((y) => y.value === selectedYear.value)?.value}
                      onChange={(value: string) => selectedYear.setValue(value)}
                      disabled={loadingDates}
                    >
                      {yearOptions.map((year) => (
                        <Select.Item value={year.value} key={year.value}>
                          {year.name}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                </div>
              )
            ) : (
              <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="start-date" style={{ display: "block", marginBottom: "4px" }}>
                    Start Date
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={customStartDate.value}
                    onChange={(e) => customStartDate.setValue(e.target.value)}
                    max={todayString}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="end-date" style={{ display: "block", marginBottom: "4px" }}>
                    End Date
                  </label>
                  <input
                    id="end-date"
                    type="date"
                    value={customEndDate.value}
                    onChange={(e) => customEndDate.setValue(e.target.value)}
                    max={todayString}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
            )}

            <Error
              error={
                dateMode === "month"
                  ? (selectedMonth.error ?? selectedYear.error)
                  : (customStartDate.error ?? customEndDate.error)
              }
            />
          </div>
        </>
      ) : (
        <>
          <div className="padding--top--m">
            <div className="margin--bottom--s">
              <DropdownWithSearch
                trigger={
                  <DropdownTrigger
                    minWidth="100%"
                    labelText="Columns"
                    displayValue={`${selectedColumns.value.length} column${selectedColumns.value.length === 1 ? "" : "s"} selected`}
                    isOpen={isColumnDropdownOpen}
                    onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
                    disabled={loadingColumns}
                    errorText={selectedColumns.error ?? undefined}
                  />
                }
                searchBar={{
                  placeholder: "Search columns",
                  value: columnSearchValue,
                  onChange: (e) => setColumnSearchValue(e.target.value),
                }}
                isOpen={isColumnDropdownOpen}
                matchTriggerWidth={true}
                onUserDismiss={() => setIsColumnDropdownOpen(false)}
              >
                {filteredColumns.map((column) => (
                  <div key={column} className="padding--x--s padding--y--xxs">
                    <CompressedCheckbox
                      label={column}
                      checked={selectedColumns.value.includes(column)}
                      onChange={() => handleColumnToggle(column)}
                    />
                  </div>
                ))}
              </DropdownWithSearch>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

export default ReportsPrefilteringModal;
